/**
 * ChatStore — the chat feature's data-access wrapper.
 *
 * Strict layering: this is the ONLY chat module that talks to the API, and it
 * does so exclusively through the {@link EngineeringVectorApi} facade —
 * `api.documents<Conversation>('conversations')` for durable transcripts,
 * `api.inference.converse` for REAL Bedrock replies, and
 * `api.identityProvider` for actor attribution. It never imports ApiClient or
 * calls fetch. React hooks call this store; pages call the hook.
 *
 * Persistence model: each conversation is one document with id `conv_<uuid>`.
 * The full transcript lives in the S3 body; renderable metadata (title,
 * modelId, messageCount, createdBy, createdAt, updatedAt, deleted) is mirrored
 * onto the DDB pointer row's index attributes so the conversation list renders
 * WITHOUT hydrating S3 bodies.
 *
 * Chat generation: {@link converse} calls the REAL Bedrock inference op
 * (`api.inference.converse`) and returns the model's outputText. It does NOT
 * swallow errors — the hook catches them and appends an inline error turn.
 *
 * ── KB-grounding ─────────────────────────────────────────────────────────────
 * The backend now exposes the Bedrock RAG operations, so this store offers BOTH
 * generation paths: {@link retrieveAndGenerate} grounds the reply in a selected
 * knowledge base (with citations), and {@link converse} remains the plain-LLM
 * fallback steered by the architecture-expert system prompt. {@link listKnowledgeBases}
 * powers the KB selector, and {@link uploadAndIngest} uploads a document to the
 * KB S3 bucket and kicks off a re-index — all through the api facade.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Deletion: the API layer exposes no delete op, so {@link softDelete} sets a
 * `deleted` flag on the document and its pointer row; queries filter it out.
 */

import type { EngineeringVectorApi } from '../../api';
import type { DocumentStore } from '../../api';
import type { StringMap } from '../../api';
import { KnowledgeBaseApi, type KbCitation, type KbSummary, type TenantKnowledgeBase } from '../../api';
import type { Conversation, ConversationSummary } from './types';
import { CHAT_MAX_TOKENS, CHAT_TEMPERATURE, formatKbQuery } from './chatModel';

export const CONVERSATIONS_COLLECTION = 'conversations';

/** Pointer-row index attribute keys. */
const ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  title: 'title',
  modelId: 'modelId',
  messageCount: 'messageCount',
  createdBy: 'createdBy',
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
  deleted: 'deleted',
} as const;

/** Arguments for a single Bedrock converse turn. */
export interface ConverseArgs {
  modelId: string;
  prompt: string;
  systemPrompt?: string;
  maxTokens?: number;
  temperature?: number;
}

/** Arguments for a KB-grounded reply (RetrieveAndGenerate). */
export interface KbGenerateArgs {
  query: string;
  sessionId?: string;
  /**
   * The registered tenant KB (bundle key) to ground the reply in — routed to
   * the backend `resolveKnowledgeBase` via the bundle-key override. When
   * omitted, the active-team default KB is used (the pre-picker behavior).
   */
  bundleKey?: string;
}

/** Normalized result of a KB-grounded reply. */
export interface KbGenerateResult {
  answer: string;
  citations: KbCitation[];
  sessionId?: string;
}

/** Result of uploading a document to the KB bucket and starting ingestion. */
export interface IngestResult {
  objectKey: string;
  ingestionJobId: string;
  ingestionStatus: string;
}

export class ChatStore {
  private readonly docs: DocumentStore<Conversation>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<Conversation>(CONVERSATIONS_COLLECTION);
  }

  /** Builds the queryable index attributes mirrored onto the pointer row. */
  private indexAttributes(c: Conversation): StringMap {
    return {
      [ROW.title]: c.title,
      [ROW.modelId]: c.modelId,
      [ROW.messageCount]: String(c.messages.length),
      [ROW.createdBy]: c.createdBy ?? '',
      [ROW.createdAt]: c.createdAt ?? '',
      [ROW.updatedAt]: c.updatedAt ?? '',
      [ROW.deleted]: c.deleted ? 'true' : 'false',
    };
  }

  /** Projects a raw pointer row into a lightweight summary. */
  private toSummary(row: StringMap): ConversationSummary {
    return {
      id: row[ROW.primaryIdentifier] ?? '',
      title: row[ROW.title] ?? '',
      modelId: row[ROW.modelId] ?? '',
      messageCount: Number(row[ROW.messageCount] ?? '0') || 0,
      createdBy: row[ROW.createdBy] ?? '',
      createdAt: row[ROW.createdAt] ?? '',
      updatedAt: row[ROW.updatedAt] ?? '',
    };
  }

  /** The alias of the current user (for seeding createdBy on new chats). */
  async currentAlias(): Promise<string> {
    const identity = await this.api.identityProvider.getIdentity();
    return identity.alias;
  }

  /**
   * Lists all non-deleted conversation summaries. Filters to this collection
   * defensively (the underlying list is not collection-scoped).
   */
  async listSummaries(): Promise<ConversationSummary[]> {
    const rows = await this.docs.list();
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === CONVERSATIONS_COLLECTION &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toSummary(r));
  }

  /** Loads a single conversation document by id (hydrates the S3 transcript). */
  async get(id: string): Promise<Conversation | undefined> {
    return this.docs.get(id);
  }

  /** Creates (persists) a new conversation document. */
  async create(conversation: Conversation): Promise<Conversation> {
    const toSave: Conversation = { ...conversation, deleted: false };
    await this.docs.save(toSave.id, toSave, this.indexAttributes(toSave));
    return toSave;
  }

  /**
   * Persists a full conversation document in place (same id). Refreshes
   * updatedAt and re-mirrors index attributes.
   */
  async save(conversation: Conversation): Promise<Conversation> {
    const updated: Conversation = {
      ...conversation,
      updatedAt: new Date().toISOString(),
      deleted: false,
    };
    await this.docs.update(updated.id, updated, this.indexAttributes(updated));
    return updated;
  }

  /**
   * Generates one assistant reply via REAL Bedrock inference and returns the
   * model's outputText. Throws on inference failure (e.g. Bedrock model access
   * not yet enabled) — the caller surfaces it as an inline error turn.
   */
  async converse(args: ConverseArgs): Promise<string> {
    const response = await this.api.inference.converse({
      modelId: args.modelId,
      prompt: args.prompt,
      ...(args.systemPrompt ? { systemPrompt: args.systemPrompt } : {}),
      maxTokens: args.maxTokens ?? CHAT_MAX_TOKENS,
      temperature: args.temperature ?? CHAT_TEMPERATURE,
    });
    return response.outputText ?? '';
  }

  /**
   * Generates one KB-grounded reply via Bedrock RetrieveAndGenerate against the
   * given knowledge base, returning the answer, parsed citations, and the
   * session id for conversational continuity. Throws on failure (e.g. KB access
   * or model not enabled) — the caller surfaces it as an inline error turn.
   */
  async retrieveAndGenerate(args: KbGenerateArgs): Promise<KbGenerateResult> {
    const response = await this.api.knowledgeBase.retrieveAndGenerate({
      query: formatKbQuery(args.query),
      ...(args.sessionId ? { sessionId: args.sessionId } : {}),
      ...(args.bundleKey ? { bundleKey: args.bundleKey } : {}),
    });
    return {
      answer: response.answer ?? '',
      citations: KnowledgeBaseApi.parseCitations(response.citations),
      ...(response.sessionId ? { sessionId: response.sessionId } : {}),
    };
  }

  /** Lists the knowledge bases available to the caller (for the KB selector). */
  async listKnowledgeBases(): Promise<KbSummary[]> {
    const response = await this.api.knowledgeBase.listKnowledgeBases({});
    return KnowledgeBaseApi.parseKnowledgeBaseList(response.body).knowledgeBases;
  }

  /**
   * Lists the tenant's REGISTERED knowledge bases (the non-secret bundle-key →
   * knowledge-base mapping) that power the chat KB picker. Distinct from
   * {@link listKnowledgeBases} (raw account Bedrock KBs) — the picker routes a
   * grounded reply by bundle key, which is what these entries carry.
   */
  async listTenantKnowledgeBases(): Promise<TenantKnowledgeBase[]> {
    const response = await this.api.knowledgeBase.listTenantKnowledgeBases();
    return KnowledgeBaseApi.parseTenantKnowledgeBaseList(response.body)
      .knowledgeBases;
  }

  /**
   * Uploads a document to the KB data-source S3 bucket (bucket resolved
   * server-side; objectKey is RELATIVE and prefixed/validated by the backend),
   * then starts an ingestion (re-index) job so the new content becomes
   * retrievable. Throws on failure (e.g. bucket access denied).
   */
  async uploadAndIngest(
    objectKey: string,
    content: string,
    contentType = 'text/plain',
  ): Promise<IngestResult> {
    const uploaded = await this.api.knowledgeBase.uploadDocument({
      objectKey,
      content,
      contentType,
    });
    const ingestion = await this.api.knowledgeBase.startIngestion();
    return {
      objectKey: uploaded.objectKey ?? objectKey,
      ingestionJobId: ingestion.ingestionJobId ?? '',
      ingestionStatus: ingestion.ingestionStatus ?? '',
    };
  }

  /**
   * Soft-deletes a conversation: fetches it, flags it deleted, and updates both
   * the S3 body and the pointer row so queries filter it out.
   */
  async softDelete(id: string): Promise<void> {
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: Conversation = {
      ...existing,
      deleted: true,
      updatedAt: new Date().toISOString(),
    };
    await this.docs.update(id, deleted, this.indexAttributes(deleted));
  }
}
